import { useCallback, useEffect, useMemo, useState } from 'react';

function getElementWidth<T>(params: {
  element: T;
  getCharacterCount: (element: T) => number;
  characterWidth: number;
  additionalPadding: number;
  getElementId?: (element: T) => string | number;
}) {
  const {
    element,
    getCharacterCount,
    characterWidth,
    additionalPadding,
    getElementId,
  } = params;
  const elementId = getElementId ? getElementId(element) : null;
  const htmlElement = elementId
    ? document.getElementById(elementId.toString())
    : null;
  const elementWidth = htmlElement
    ? htmlElement.offsetWidth
    : getCharacterCount(element) * characterWidth + additionalPadding;
  return elementWidth;
}

export function useMeasureAndTruncate<T>(
  elements: T[],
  containerRef: React.RefObject<HTMLDivElement>,
  options: {
    // gets character count of the element
    getCharacterCount: (element: T) => number;
    // The width each character takes up
    characterWidth: number;
    // additional padding to the width of the element
    additionalPadding: number;
    // gap between elements
    elementGap: number;
    // width reserved other items inside the containerRef that should be excluded fromthis calculation
    // it is added to the width calculation of the elements.
    reservedWidth: number;
    // if true, the reservedWidth will be enforced
    enforceReservedWidth?: boolean;
    // id of the active element
    activeElementId?: string | number;
    // gets the id of the element
    getElementId?: (element: T) => string | number;
  }
) {
  const [visible, setVisible] = useState<T[]>([]);
  const [overflow, setOverflow] = useState<T[]>([]);

  const memoizedOptions = useMemo(
    () => options,
    [
      options.characterWidth,
      options.additionalPadding,
      options.reservedWidth,
      options.activeElementId,
      options.enforceReservedWidth,
      options.elementGap,
    ]
  );

  const measureAndTruncate = useCallback(
    (containerWidth?: number) => {
      if (!containerWidth) return;

      if (!elements.length) {
        setVisible([]);
        setOverflow([]);
        return;
      }

      const totalWidth = elements.reduce(
        (acc, el, i) =>
          acc +
          getElementWidth({
            element: el,
            getCharacterCount: memoizedOptions.getCharacterCount,
            characterWidth: memoizedOptions.characterWidth,
            additionalPadding: memoizedOptions.additionalPadding,
            getElementId: memoizedOptions.getElementId,
          }) +
          (i !== 0 ? memoizedOptions.elementGap : 0),
        0
      );

      let currentWidth = memoizedOptions.reservedWidth;
      if (
        totalWidth <= Math.round(containerWidth) &&
        !memoizedOptions.enforceReservedWidth
      ) {
        currentWidth = 0;
      }

      // Use memoizedOptions instead of options

      const visible: T[] = [];
      const overflow: T[] = [];

      const activeElement = elements.find(
        (el) =>
          memoizedOptions.getElementId &&
          memoizedOptions.getElementId(el) === memoizedOptions.activeElementId
      );

      if (activeElement) {
        const activeElementWidth = getElementWidth({
          element: activeElement,
          getCharacterCount: memoizedOptions.getCharacterCount,
          characterWidth: memoizedOptions.characterWidth,
          additionalPadding: memoizedOptions.additionalPadding,
          getElementId: memoizedOptions.getElementId,
        });

        visible.push(activeElement);
        currentWidth += activeElementWidth;
      }

      let overflowed = false;

      elements.forEach((el) => {
        if (el !== activeElement) {
          const elementWidth = getElementWidth({
            element: el,
            getCharacterCount: memoizedOptions.getCharacterCount,
            characterWidth: memoizedOptions.characterWidth,
            additionalPadding: memoizedOptions.additionalPadding,
            getElementId: memoizedOptions.getElementId,
          });

          if (!overflowed && currentWidth + elementWidth <= containerWidth) {
            visible.push(el);
            currentWidth += elementWidth;
          } else {
            overflowed = true;
            overflow.push(el);
          }
        }
      });
      setVisible(visible);
      setOverflow(overflow);
    },
    [elements, memoizedOptions]
  );

  useEffect(() => {
    measureAndTruncate(containerRef.current?.offsetWidth);
    const handleResize = () => {
      measureAndTruncate(containerRef.current?.offsetWidth);
    };
    const resizeObserver = new ResizeObserver((targets) => {
      measureAndTruncate(targets[0].contentRect.width);
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [measureAndTruncate]);

  return { visible, overflow };
}
